import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ChevronDown, ChevronUp, ExternalLink, Copy, Check, ListChecks } from 'lucide-react';
import { Button } from '../ui/button';

// Passo a passo guiado da conexão MANUAL com a Meta (Cloud API), usado enquanto
// o SinTicket ainda não é Tech Provider aprovado (sem Embedded Signup
// disponível para clientes). Consolida em um único lugar, na ordem certa, os
// passos hoje espalhados entre developers.facebook.com e business.facebook.com
// — ver META_TECH_PROVIDER_GUIDE.md, Seção "Fase 1".
//
// O progresso (checkboxes) fica em localStorage só para não se perder se o
// usuário navegar para o Meta Business Suite em outra aba e voltar — não tem
// nenhum significado no backend.
const STORAGE_KEY = 'sinticket:meta-manual-wizard-steps';

const getWebhookUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  return apiUrl.replace(/\/api\/?$/, '') + '/webhook/meta';
};

interface Step {
  id: string;
  title: string;
  description: string;
  subitems?: string[];
  linkUrl?: string;
  linkLabel?: string;
  copyValue?: string;
  copyLabel?: string;
  note?: string;
}

// Textos no i18n (metaGuide.steps.<chave>); aqui ficam só ids, links e a ordem.
const buildSteps = (t: TFunction): Step[] => [
  {
    id: 'app',
    title: t('metaGuide.steps.app.title'),
    description: t('metaGuide.steps.app.description'),
    subitems: t('metaGuide.steps.app.subitems', { returnObjects: true }) as string[],
    linkUrl: 'https://developers.facebook.com/apps/creation/',
    linkLabel: t('metaGuide.steps.app.linkLabel'),
    note: t('metaGuide.steps.app.note'),
  },
  {
    id: 'webhook',
    title: t('metaGuide.steps.webhook.title'),
    description: t('metaGuide.steps.webhook.description'),
    copyValue: getWebhookUrl(),
    copyLabel: t('metaGuide.steps.webhook.copyLabel'),
    note: t('metaGuide.steps.webhook.note'),
  },
  {
    id: 'system-user',
    title: t('metaGuide.steps.systemUser.title'),
    description: t('metaGuide.steps.systemUser.description'),
    linkUrl: 'https://business.facebook.com/settings/system-users',
    linkLabel: t('metaGuide.steps.systemUser.linkLabel'),
    note: t('metaGuide.steps.systemUser.note'),
  },
  {
    id: 'app-secret',
    title: t('metaGuide.steps.appSecret.title'),
    description: t('metaGuide.steps.appSecret.description'),
    linkUrl: 'https://developers.facebook.com/apps/',
    linkLabel: t('metaGuide.steps.appSecret.linkLabel'),
  },
  {
    id: 'paste',
    title: t('metaGuide.steps.paste.title'),
    description: t('metaGuide.steps.paste.description'),
  },
];

const copyToClipboard = async (value: string) => {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
};

const MetaManualSetupGuide: React.FC<{ defaultOpen?: boolean }> = ({ defaultOpen = true }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const steps = buildSteps(t);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setChecked(JSON.parse(raw));
    } catch {
      // localStorage indisponível — segue sem persistir progresso.
    }
  }, []);

  const toggleStep = (id: string) => {
    setChecked((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };

  const handleCopy = async (id: string, value: string) => {
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopiedId(id);
      setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1500);
    }
  };

  const doneCount = steps.filter((s) => checked[s.id]).length;

  return (
    <div className="rounded-md border dark:border-gray-600 mb-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 p-3 text-left"
      >
        <div className="flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-blue-600 shrink-0" />
          <span className="text-sm font-medium dark:text-gray-100">
            {t('metaGuide.title', { done: doneCount, total: steps.length })}
          </span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          <p className="text-xs text-muted-foreground">
            {t('metaGuide.intro')}
          </p>
          {steps.map((step) => (
            <div key={step.id} className="flex gap-2 rounded-md bg-muted/40 p-2">
              <input
                type="checkbox"
                checked={!!checked[step.id]}
                onChange={() => toggleStep(step.id)}
                className="mt-1 rounded shrink-0"
              />
              <div className="space-y-1 min-w-0">
                <p className={`text-sm font-medium dark:text-gray-100 ${checked[step.id] ? 'line-through opacity-60' : ''}`}>
                  {step.title}
                </p>
                <p className="text-xs text-muted-foreground">{step.description}</p>
                {step.subitems && (
                  <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1">
                    {step.subitems.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                )}
                {step.note && (
                  <p className="text-xs text-yellow-700 dark:text-yellow-400">{step.note}</p>
                )}
                {step.linkUrl && (
                  <a
                    href={step.linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 underline"
                  >
                    {step.linkLabel} <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {step.copyValue && (
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-xs bg-background border rounded px-2 py-1 truncate max-w-full">{step.copyValue}</code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 w-7 p-0 shrink-0"
                      onClick={() => handleCopy(step.id, step.copyValue as string)}
                      title={step.copyLabel}
                    >
                      {copiedId === step.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MetaManualSetupGuide;
